import { cn } from "@/lib/utils";
import { FLOW_COLOR } from "./flowColors";
import type { StageBarCounts } from "./StageBar";
const DOT_STAGES = ["new", "booked", "onHold", "inTransit"] as const;
const MAX_DOTS = 12;
const dotsOf = (stages: StageBarCounts, behind: StageBarCounts) => DOT_STAGES.flatMap(stage => {
  const late = Math.min(behind[stage], stages[stage]);
  return Array.from({
    length: stages[stage]
  }, (_, index) => index < late ? FLOW_COLOR.behind : FLOW_COLOR[stage]);
});
export const capDots = (dots: string[], max: number): string[] => {
  if (dots.length <= max) return dots;
  const runs = dots.reduce<{
    color: string;
    count: number;
  }[]>((acc, color) => {
    const last = acc[acc.length - 1];
    if (last?.color === color) last.count += 1;else acc.push({
      color,
      count: 1
    });
    return acc;
  }, []);
  if (runs.length >= max) return runs.slice(0, max).map(run => run.color);
  const spare = max - runs.length;
  const extra = dots.length - runs.length;
  const shares = runs.map(run => (run.count - 1) * spare / extra);
  const counts = shares.map(Math.floor);
  const leftover = spare - counts.reduce((sum, count) => sum + count, 0);
  shares.map((share, index) => ({
    index,
    fraction: share - counts[index]
  })).sort((a, b) => b.fraction - a.fraction || a.index - b.index).slice(0, leftover).forEach(({
    index
  }) => {
    counts[index] += 1;
  });
  return runs.flatMap((run, index) => Array.from({
    length: counts[index] + 1
  }, () => run.color));
};
export function FlowRow({
  name,
  place,
  summary,
  stages,
  behind,
  isMuted = false,
  className
}: {
  name: string;
  place?: string | null;
  summary: string;
  stages?: StageBarCounts;
  behind?: StageBarCounts;
  isMuted?: boolean;
  className?: string;
}) {
  const allDots = stages && behind ? dotsOf(stages, behind) : [];
  const dots = capDots(allDots, MAX_DOTS);
  const hiddenDotCount = allDots.length - dots.length;
  return <div className={cn("flex min-w-0 flex-col whitespace-nowrap", className)} style={{
    gap: 6
  }} data-sentry-component="FlowRow" data-sentry-source-file="FlowRow.tsx">
            <div className="flex min-w-0 items-baseline gap-2">
                <span title={name} className={cn("max-w-full shrink-0 truncate", isMuted ? "text-ink-2" : "text-ink")} style={{
        fontSize: 14.5,
        letterSpacing: "-0.005em"
      }}>
                    {name}
                </span>
                {place && <span className="min-w-0 truncate text-ink-3" style={{
        fontSize: 12
      }}>
                        {place}
                    </span>}
            </div>
            <div className="flex items-center" style={{
      gap: 10
    }}>
                {dots.length > 0 && <span role="img" aria-label={summary} data-stages={JSON.stringify(stages)} className="flex items-center" style={{
        gap: 4
      }}>
                        {dots.map((color, index) => <span key={index} className="shrink-0 rounded-full" style={{
          width: 6,
          height: 6,
          background: color
        }} />)}
                        {hiddenDotCount > 0 && <span className="tabular-nums text-ink-3" style={{
          fontSize: 11
        }}>
                                +{hiddenDotCount}
                            </span>}
                    </span>}
                <span className="tabular-nums text-ink-2" style={{
        fontSize: 12.5
      }}>
                    {summary}
                </span>
            </div>
        </div>;
}